import type { IEvents } from '@/assets/types/api/Event';
import type { IResources } from '@/assets/types/api/Resources';

export const useWebinarsStore = defineStore('webinarsStore', () => {
  const nuxtApp = useNuxtApp();

  /* State */

  const events = ref<IEvents>({
    pageIndex: 1,
    pageSize: 6,
    totalCount: 0,
    items: [],
  });
  const resources = ref<IResources>({
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0,
    items: [],
  });
  const searchKeyword = ref<string>('');
  const isScrollEvent = ref<boolean>(false);
  const isScrollResource = ref<boolean>(false);

  /* Getters */

  const isNoEvent = computed(
    (): boolean =>
      events.value.pageIndex !== -1 && events.value.items.length === 0
  );

  const isNoResource = computed(
    (): boolean =>
      resources.value.pageIndex !== -1 && resources.value.items.length === 0
  );

  const totalEventPage = computed((): number => {
    const total = events.value.totalCount;
    const quantity = events.value.pageSize;
    return total <= 0
      ? 0
      : total % quantity === 0
        ? total / quantity
        : Math.floor(total / quantity) + 1;
  });

  const totalResourcePage = computed((): number => {
    const total = resources.value.totalCount;
    const quantity = resources.value.pageSize;
    return total <= 0
      ? 0
      : total % quantity === 0
        ? total / quantity
        : Math.floor(total / quantity) + 1;
  });

  /* Actions */

  const USE_GET_EVENTS = async () => {
    /**
     * 取得 Events 清單
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    const query = computed(() => ({
      websiteCode: locale.value,
      pageIndex: events.value.pageIndex,
      pageSize: events.value.pageSize,
    }));

    return await useCustomFetch(() => '/marketplace/api/v1/events/event', {
      method: 'GET',
      lazy: true,
      immediate: true,
      query,
      onResponse: ({ response }) => {
        const { _data, status } = response;

        if (status >= 200 && status < 300) {
          const data = _data as IEvents;

          if (isScrollEvent.value) {
            events.value.items = [...events.value.items, ...data.items];
            isScrollEvent.value = false;
          } else {
            events.value.items = [...data.items];
          }
          events.value.pageIndex = data.pageIndex;
          events.value.pageSize = data.pageSize;
          events.value.totalCount = data.totalCount;
        } else {
          console.error(_data);
          $notification({
            message: (_data as { message: string }).message,
            type: 'error',
          });
        }
      },
    });
  };

  const USE_GET_RESOURCES = async () => {
    /**
     * 搜尋資源素材資訊
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;

    const isCN = locale.value === EnumLocale.ZH_CN;

    const resourceTypeCodes = isCN ? ['videos'] : ['webinars'];

    const query = computed(() => ({
      websiteCode: locale.value,
      pageIndex: resources.value.pageIndex,
      pageSize: resources.value.pageSize,
      resourceTypeCodes,
      keyword: searchKeyword.value,
    }));

    return await useCustomFetch(() => '/marketplace/api/v1/resources/search', {
      method: 'GET',
      lazy: true,
      immediate: true,
      query,
      onResponse: ({ response }) => {
        const { _data, status } = response;

        if (status >= 200 && status < 300) {
          const data = _data as IResources;
          if (isScrollResource.value) {
            resources.value.items = [...resources.value.items, ...data.items];
            isScrollResource.value = false;
          } else {
            resources.value.items = [...data.items];
          }
          resources.value.pageIndex = data.pageIndex;
          resources.value.pageSize = data.pageSize;
          resources.value.totalCount = data.totalCount;
        } else {
          console.error(_data);
          $notification({
            message: (_data as { message: string }).message,
            type: 'error',
          });
        }
      },
    });
  };

  return {
    events,
    resources,
    searchKeyword,
    isScrollEvent,
    isScrollResource,
    isNoEvent,
    isNoResource,
    totalEventPage,
    totalResourcePage,
    USE_GET_EVENTS,
    USE_GET_RESOURCES,
  };
});
